import { useState } from "react";
import { Check, ChevronDown, Settings2 } from "lucide-react";
import { Pill } from "@/components/rp";
import { fmtDate, PHASE_STATUS, PHASE_TONE } from "@/lib/format";

const TaskRow = ({ task, admin, onToggle }) => {
  const Comp = admin ? "button" : "div";
  return (
    <Comp
      type={admin ? "button" : undefined}
      onClick={admin ? () => onToggle(task) : undefined}
      data-testid={`task-${task.id}`}
      className={`w-full flex items-center gap-3 py-2.5 text-left text-[15px] ${admin ? "hover:bg-white/60 rounded-xl px-2 -mx-2 transition-colors" : ""}`}
    >
      <span className={`h-5 w-5 shrink-0 rounded-full flex items-center justify-center border transition-colors ${task.done ? "bg-[#070707] border-[#070707]" : "border-[#C8BDAB] bg-white"}`}>
        {task.done && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </span>
      <span className={task.done ? "text-black" : "text-[#757575]"}>{task.title}</span>
    </Comp>
  );
};

const PhaseCard = ({ phase, index, open, onToggleOpen, admin, onToggleTask, onManage }) => {
  const done = phase.tasks.filter((t) => t.done).length;
  const active = phase.status === "active";
  return (
    <div className={`rounded-[20px] border transition-colors ${active ? "bg-[#E6DDCE]/40 border-[#C8BDAB]" : "bg-[#F7F7F7] border-[#EBEBEB]"}`} data-testid={`phase-${phase.key}`}>
      <button type="button" onClick={onToggleOpen} className="w-full flex items-center gap-4 p-5 text-left" data-testid={`phase-toggle-${phase.key}`}>
        <span className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold ${phase.status === "done" ? "bg-[#070707] text-white" : active ? "bg-[#C8BDAB] text-black" : "bg-[#EBEBEB] text-[#757575]"}`}>
          {phase.status === "done" ? <Check className="h-4 w-4" /> : index + 1}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-medium">{phase.name}</span>
          <span className="block text-xs text-[#757575] mt-0.5">{done}/{phase.tasks.length} attività</span>
        </span>
        <Pill tone={PHASE_TONE[phase.status]} data-testid={`phase-status-${phase.key}`}>{PHASE_STATUS[phase.status]}</Pill>
        <ChevronDown className={`h-4 w-4 text-[#757575] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1 pl-[72px] max-[480px]:pl-5">
          {phase.tasks.map((t) => <TaskRow key={t.id} task={t} admin={admin} onToggle={onToggleTask} />)}
          {!phase.tasks.length && <p className="text-sm text-[#757575] py-2">Nessuna attività.</p>}
          {phase.note && (
            <div className="mt-3 rounded-2xl bg-[#E6DDCE] p-4" data-testid={`phase-note-${phase.key}`}>
              <p className="rp-label text-black/60">Nota RP Home · {fmtDate(phase.updated_at)}</p>
              <p className="text-sm mt-1.5 leading-relaxed">{phase.note}</p>
            </div>
          )}
          {admin && (
            <button onClick={() => onManage(phase)} data-testid={`phase-manage-${phase.key}`} className="mt-4 inline-flex items-center gap-2 text-sm font-medium hover:underline">
              <Settings2 className="h-4 w-4" /> Gestisci fase
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const PhaseList = ({ phases, admin = false, onToggleTask, onManage }) => {
  const activeKey = phases.find((p) => p.status === "active")?.key;
  const [open, setOpen] = useState(() => new Set(activeKey ? [activeKey] : []));
  const toggle = (k) => setOpen((s) => {
    const n = new Set(s);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });
  return (
    <div className="space-y-3" data-testid="phase-list">
      {phases.map((p, i) => (
        <PhaseCard key={p.key} phase={p} index={i} open={open.has(p.key)} onToggleOpen={() => toggle(p.key)} admin={admin} onToggleTask={onToggleTask} onManage={onManage} />
      ))}
    </div>
  );
};
